import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector, connect } from "react-redux";
import isEmpty from "lodash/isEmpty";
import flattenDeep from "lodash/flattenDeep";
import pick from "lodash/pick";
import isEqual from "lodash/isEqual";
import mapValues from "lodash/mapValues";
import { v4 as uuidv4 } from "uuid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faTimes,
  faPeopleArrows,
  faNotesMedical,
  faGripLinesVertical,
  faUsers,
  faCalendarAlt,
  faGenderless,
  faGlobe,
  faUserFriends,
} from "@fortawesome/free-solid-svg-icons";
import withErrorGuard from "../../../../HOC/withErrorGuard";
import {
  ColoredPill,
  Table,
  Loading,
  ToggleButton,
  CnvClassificationPill,
  SampleFindingsTable,
} from "../../../../components/atoms";
import {
  getCnvAnnotation,
  getCnvVersion,
  getCnvClassifications,
  getCnvVerdict,
  getCnvSampleFindings,
  getVariantInfo,
} from "../../../../state/selectors/CnvComponent.selectors";
import {
  getACMGClassifierHasErrors,
  getApiUrl,
  getCnvClassifierErrors,
} from "../../../../state/selectors/general.selectors";
import ls from "../../../../common/helpers/localStorageActions";
import useRefState from "../../../../hooks/useRefState";
import usePrevious from "../../../../hooks/usePrevious";
import { getVarsoUrl } from "../../../../common/helpers/helpers";
import {
  getCnvACMGAnnotationFindings,
  getVariantQueryMode,
} from "../../../../state/selectors/CNVs.selectors";
import {
  SEX_ABBREVIATIONS_TO_SEX,
  ETHINICITES_ABBREVIATIONS_TO_ETHNICITIES,
} from "../../../../common/helpers/Constant";
import SearchModal from "../../../../components/atoms/SearchModal/SearchModal";
import ThrottlingMessage from "../../../../components/atoms/ThrottlingMessage";
import ComponentPanel from "../../../../components/atoms/ComponentPanel/ComponentPanel";
import { httpRequest } from "../../../../services/http";
import { defaultPathogenicityColor } from "../../../../common/helpers/colors";
import LinkifyBase from "../../../../components/social/LinkifyBase";

const familyMemberAffectedMap = {
  True: "Yes",
  False: "No",
  Unknown: "Unknown",
};

const CnvACMG = ({
  variantQueryMode,
  acmgSampleFindings,
  hasACMGErrors,
  cnvClassificationErrors,
}) => {
  const cnvAnnotation = useSelector(getCnvAnnotation);
  const [rows, setRows] = useState([]);
  const [isVerdictLoading, setVerdictLoading] = useState(false);
  const [rulesList, rulesListRef, setRulesList] = useRefState([]);
  const [isSearchModalOpen, setSearchModalOpen] = useState(false);
  const previousRulesList = usePrevious(rulesList);
  const cnvVerdictSelector = useSelector(getCnvVerdict);
  const [cnvVerdict, setCnvVerdict] = useState([]);
  const [showUncertainSignificane, setUncertainVisibility] = useState(
    ls.getCnvClassificationFullDetail() || false
  );
  const previousUncertainSignificane = usePrevious(showUncertainSignificane);
  const version = useSelector(getCnvVersion);
  const host = useSelector((state) => state.varsomeHost);
  const svRules = useSelector((state) => state.svRules);
  const isLoading = useSelector((state) => state.loading);
  const cnvClassifications = useSelector(getCnvClassifications);
  const previousCnvClassifications = usePrevious(cnvClassifications);
  const cnvSampleFindings = useSelector(getCnvSampleFindings);
  const variantInfo = useSelector(getVariantInfo);
  const apiUrl = useSelector(getApiUrl);
  const cnvHeaderUrl = getVarsoUrl(
    "https://www.ncbi.nlm.nih.gov/pmc/articles/PMC7313390/"
  );
  const pathogenicityList = [
    "Pathogenic",
    "Likely Pathogenic",
    "Uncertain Significance",
    "Likely Benign",
    "Benign",
  ];
  const tierLabelsMapping = {
    Pathogenic: "P",
    "Likely Pathogenic": "LP",
    "Uncertain Significance": "VUS",
    "Likely Benign": "LB",
    Benign: "B",
  };
  const rulesMapping = {
    Content: "Genomic Content",
    Overlap: "Gene/Regions Overlap",
    Gene: "Gene",
    Literature: "Literature",
    Inheritance: "Inheritance",
  };

  const getTooltipContent = (svInfo) => (
    <div
      style={{
        maxWidth: "300px",
      }}
    >
      <div className="tw-font-bold">Definition</div>
      <div className="tw-font-normal">{svInfo.description}</div>
      <div>
        <br />
        <span>
          {svInfo && svInfo.implemented_in_thalia ? (
            <div className="tw-font-bold">
              Implemented
              <FontAwesomeIcon
                icon={faCheck}
                size="lg"
                fixedWidth={true}
                className="tw-px-1 tw-text-green-600"
              />
            </div>
          ) : (
            <div className="tw-font-bold">
              Not implemented
              <FontAwesomeIcon
                icon={faTimes}
                size="lg"
                fixedWidth={true}
                className="tw-px-1 tw-text-red-500"
              />
            </div>
          )}
        </span>
      </div>
    </div>
  );

  const headers = [
    { id: 1, title: "Classifications" },
    {
      id: 2,
      title: (
        <div className="tw-flex tw-items-center tw-justify-end">
          <ToggleButton
            onToggleClick={(e) => {
              setUncertainVisibility(e.target.checked);
              ls.setCnvClassificationFullDetail(e.target.checked);
            }}
            label="Show full detail"
            isChecked={showUncertainSignificane}
            noPaddingAndMargin={true}
          />
        </div>
      ),
    },
  ];

  const firstColumnStyles = {
    width: "22rem",
    verticalAlign: "top",
    textAlign: "center",
  };

  const secondColumnStyles = {
    verticalAlign: "top",
    color: defaultPathogenicityColor,
  };

  const getTierChange = (changedRulesList) => {
    let rules = "";

    changedRulesList.forEach((t) => {
      rules += `&rules=${t.ruleName}_${
        tierLabelsMapping[t.overridenPathogenicity || t.originalPathogenicity]
      }`;
    });
    const url = `${apiUrl}/sv-verdict?${rules}`;

    setVerdictLoading(true);
    httpRequest
      .get(url)
      .then((response) => {
        const { data } = response;
        if (data.SV_rules) {
          setCnvVerdict(data.SV_rules);
        }
      })
      .catch((error) => {
        console.error("Error while getting CNV verdict", error);
      })
      .finally(() => {
        setVerdictLoading(false);
      });
  };

  const onClassChange = (rule, pathogenicity) => {
    const rulesToChange = rulesListRef.current.map((r) =>
      r.ruleName === rule
        ? {
            ...r,
            overridenPathogenicity: pathogenicity.value,
          }
        : r
    );

    setRulesList(rulesToChange);
    getTierChange(rulesToChange);
  };

  const getInitialRulesList = () => {
    if (!cnvClassifications) return [];

    const initialRulesList = cnvClassifications.map((cc) => {
      const ov = rulesList.filter((r) => r.ruleName === cc.name);

      return {
        ruleName: cc.name,
        originalPathogenicity: cc.saphetor_class,
        overridenPathogenicity:
          (ov && ov[0] && ov[0].overridenPathogenicity) || undefined,
      };
    });
    setRulesList(initialRulesList);

    return initialRulesList;
  };

  const calculateRows = () => {
    if (!cnvClassifications) return;
    const valueToFilter = "Uncertain Significance";
    const rowsToDisplay = [];

    const filteredCnvClassifications = cnvClassifications
      .filter((f) =>
        showUncertainSignificane ? f : f.saphetor_class !== valueToFilter
      )
      // when the show full details is unchecked it show only
      // the first user explain that matches the tier
      .map((s) => {
        return showUncertainSignificane
          ? s
          : {
              ...s,
              saphetor_user_explain: mapValues(
                pick(s.saphetor_user_explain, [s.saphetor_class]),
                (v) => {
                  // only the first value
                  return [v[0]];
                }
              ),
            };
      });

    const initialRulesList = getInitialRulesList();
    filteredCnvClassifications.map((c) => {
      let classKeys = c.saphetor_user_explain
        ? Object.keys(c.saphetor_user_explain)
        : [];
      const selectOptions = pathogenicityList.map((p) => ({
        value: p,
        label: p,
      }));

      if (!showUncertainSignificane) {
        classKeys = classKeys.filter((t) => t !== "Uncertain Significance");
      }
      const userExplainRows =
        c.saphetor_user_explain &&
        classKeys.map((k) => {
          return c.saphetor_user_explain[k];
        });
      const flattenedUserExplainRows = userExplainRows
        ? flattenDeep(userExplainRows)
        : [""];
      const keysAndLen = classKeys.map((key) => ({
        name: key,
        len: c.saphetor_user_explain[key].length,
      }));

      const userExplainColumn =
        keysAndLen &&
        keysAndLen.length > 0 &&
        flattenedUserExplainRows.map((e, index) => {
          const selectedRule = initialRulesList.filter(
            (r) => r.ruleName === c.name
          )[0];
          const pathogenicityValue =
            selectedRule.overridenPathogenicity ||
            selectedRule.originalPathogenicity;

          return {
            id: uuidv4(),
            columns: [
              index === 0
                ? {
                    id: uuidv4(),
                    content: (
                      <React.Fragment>
                        <ColoredPill
                          colorParams={{
                            type: "pathogenicity",
                            value: pathogenicityValue,
                          }}
                          content={
                            <CnvClassificationPill
                              tooltipContent={getTooltipContent}
                              name={rulesMapping[c.name] || c.name}
                              svRules={svRules}
                              options={selectOptions}
                              onChange={(selectEvent) =>
                                onClassChange(c.name, selectEvent)
                              }
                              value={{
                                value: pathogenicityValue,
                                label: pathogenicityValue,
                              }}
                              extraStyles={{
                                flexDirection: "column",
                              }}
                              size="190px"
                            />
                          }
                        />
                      </React.Fragment>
                    ),
                    rowSpan: flattenedUserExplainRows.length,
                    styles: firstColumnStyles,
                  }
                : [],
              {
                id: uuidv4(),
                content: <LinkifyBase txt={e} />,
                rowSpan: 1,
                styles: secondColumnStyles,
                isDisabled:
                  selectedRule.overridenPathogenicity &&
                  selectedRule.overridenPathogenicity !==
                    selectedRule.originalPathogenicity,
              },
            ],
          };
        });

      // if empty array is returned from the map ignore it
      if (!isEmpty(userExplainColumn)) {
        rowsToDisplay.push(...userExplainColumn);
      }

      return c;
    });
    setRows(rowsToDisplay);
  };

  const shouldShowVariantInfo = () =>
    variantInfo &&
    (variantInfo.chromo ||
      variantInfo.geneSymbol ||
      variantInfo.type ||
      variantInfo.pos ||
      variantInfo.end ||
      variantInfo.length);

  const getVariantInfoData = () =>
    [
      { title: "Chromosome", value: variantInfo.chromo },
      { title: "Gene symbol", value: variantInfo.geneSymbol },
      { title: "Type", value: variantInfo.type },
      { title: "Position", value: variantInfo.pos },
      { title: "End", value: variantInfo.end },
      { title: "Length", value: variantInfo.length },
    ]
      .map((v) => v.value && v)
      .filter((v) => !!v);

  const getCNVDocumentationLink = (
    <span className="tw-float-right">
      <span className="tw-mr-5">
        <a href={`${host}/about/resources/sv-implementation/`} target="_blank">
          Documentation
        </a>
      </span>
    </span>
  );

  useEffect(() => {
    setCnvVerdict(cnvVerdictSelector);
  }, [cnvVerdictSelector]);

  useEffect(() => {
    if (previousUncertainSignificane !== showUncertainSignificane) {
      calculateRows();
    }
  }, [showUncertainSignificane]);

  useEffect(() => {
    if (!isEqual(previousRulesList, rulesList)) {
      calculateRows();
    }
  }, [rulesList]);

  // everytime the ovveriden rules change change the ruleslist
  useEffect(() => {
    if (!isEqual(previousCnvClassifications, cnvClassifications)) {
      calculateRows();
    }
  }, [cnvClassifications]);

  const {
    phenotypes,
    diseases,
    zygosity,
    inheritance,
    age,
    ethnicity,
    sex,
    familyMembersAffected,
    familySegregation,
    annotation_mode: annotationMode,
  } = variantQueryMode;
  const ethnicityFromAbbreviation =
    ethnicity && ETHINICITES_ABBREVIATIONS_TO_ETHNICITIES[ethnicity];
  const sexFromAbbreviation = sex && SEX_ABBREVIATIONS_TO_SEX[sex];
  let phenotypesDiseases = "";
  if (
    phenotypes !== "" &&
    phenotypes !== undefined &&
    Array.isArray(phenotypes) &&
    phenotypes.length > 0
  ) {
    if (
      diseases !== "" &&
      diseases !== undefined &&
      Array.isArray(diseases) &&
      diseases.length > 0
    ) {
      phenotypesDiseases = [...phenotypes, ...diseases];
    } else {
      phenotypesDiseases = [...phenotypes];
    }
  } else if (
    diseases !== "" &&
    diseases !== undefined &&
    Array.isArray(diseases) &&
    diseases.length > 0
  ) {
    phenotypesDiseases = [...diseases];
  }

  const finalPhenotypes = __IS_COMPONENTS_PORTAL__
    ? phenotypes
    : phenotypesDiseases;

  const finalDiseases = __IS_COMPONENTS_PORTAL__ ? diseases : "";
  const throttling = cnvAnnotation?.throttling_info;
  const content = !throttling ? (
    <div className="varsome-inner-container">
      <div className="tw-flex tw-items-center tw-flex-col tw-font-semibold">
        <div className="tw-text-lg tw-font-semibold tw-h-full">
          {isVerdictLoading ? (
            <Loading />
          ) : (
            cnvVerdict &&
            !hasACMGErrors && (
              <ColoredPill
                colorParams={{
                  type: "pathogenicity",
                  value: cnvVerdict.saphetor_class,
                }}
                content={cnvVerdict.saphetor_class}
              />
            )
          )}
          {!isVerdictLoading && hasACMGErrors && cnvClassificationErrors && (
            <div
              className="varsome-text-center tw-text-base tw-font-normal"
              style={{ color: "red" }}
            >
              <i className="fa fa-exclamation-circle" />
              {`
              There has been an internal error processing the ACMG
              classification for this variant in rule${
                cnvClassificationErrors.length > 1 ? "s: " : ": "
              } ${cnvClassificationErrors.join()}`}
            </div>
          )}
        </div>
        {__IS_COMPONENTS_PORTAL__ && (
          <button
            type="button"
            className="btn btn-success tw-mt-1"
            onClick={() =>
              window.clinicalResults.classifyVariantFromComponent(
                rulesList
                  .filter(
                    (rule) =>
                      (typeof rule.overridenPathogenicity !== "undefined" &&
                        rule.overridenPathogenicity !==
                          "Uncertain Significance") ||
                      rule.originalPathogenicity !== "Uncertain Significance"
                  )
                  .map((rule) => rule.ruleName),
                cnvVerdict.saphetor_class
              )
            }
          >
            Save as manual classification
          </button>
        )}
        {/* inheritance description */}
        {cnvSampleFindings && cnvSampleFindings.inheritance && (
          <div className="tw-flex tw-text-xs tw-items-center tw-pt-2 tw-pb-4">
            {cnvSampleFindings.inheritance}
          </div>
        )}
      </div>
      {!__IS_COMPONENTS_PORTAL__ && (
        <SearchModal
          show={isSearchModalOpen}
          handleClose={() => {
            setSearchModalOpen(false);
          }}
        />
      )}
      {annotationMode === "germline" && (
        <div className="">
          <SampleFindingsTable
            onEditClick={() => setSearchModalOpen(true)}
            sampleFindings={acmgSampleFindings}
            rows={[
              {
                icon: faPeopleArrows,
                label: "Phenotypes",
                content: "phenotypes",
                submittedData: finalPhenotypes,
              },
              {
                icon: faUsers,
                label: "Inheritance",
                content: "inheritance",
                submittedData: inheritance,
              },
              {
                icon: faUserFriends,
                label: "Family members affected?",
                content: "Family members affected?",
                submittedData: familyMemberAffectedMap[familyMembersAffected],
              },
              {
                icon: faUserFriends,
                label: "Family Segregation",
                content: "Family Segregation",
                submittedData: familyMemberAffectedMap[familySegregation],
              },
              {
                icon: faGripLinesVertical,
                label: "Zygosity",
                content: "zygosity",
                submittedData: zygosity,
              },
              {
                icon: faGenderless,
                label: "Sex",
                content: "sex",
                submittedData: sexFromAbbreviation,
              },
              {
                icon: faCalendarAlt,
                label: "Age at onset",
                content: "age",
                submittedData: age,
              },
              {
                icon: faGlobe,
                label: "Ethnicity",
                content: "ethnicity",
                submittedData: ethnicityFromAbbreviation,
              },
              {
                icon: faNotesMedical,
                label: "Diseases",
                content: "disease",
                submittedData: finalDiseases,
              },
              {
                icon: faNotesMedical,
                label: "Mode of Inheritance",
                content: "mode_of_inheritance",
                // submittedData refers to the user's input.
                // In case of mode_of_inheritance we don't have a user input but we do have sample findings.
                submittedData: [],
              },
            ]}
          />
        </div>
      )}
      {!__IS_COMPONENTS_PORTAL__ &&
        shouldShowVariantInfo() &&
        getVariantInfoData().length > 0 &&
        !hasACMGErrors && (
          <div className="tw-flex tw-w-1/3 tw-my-6">
            <Table
              headers={[
                { id: 1, title: "Variant Info" },
                { id: 2, title: "" },
              ]}
              rows={getVariantInfoData().map((d) => ({
                id: d.title,
                columns: [
                  {
                    id: d.title,
                    content: (
                      <div className="tw-font-semibold tw-text-xs">
                        {d.title}
                      </div>
                    ),
                    styles: {
                      width: "7rem",
                    },
                  },
                  {
                    id: d.value,
                    content: (
                      <div className="tw-text-xs">
                        {Array.isArray(d.value) ? (
                          <div>{d.value.map((v) => `${v} `)}</div>
                        ) : (
                          d.value
                        )}
                      </div>
                    ),
                  },
                ],
              }))}
            />
          </div>
        )}
      {!hasACMGErrors && <Table headers={headers} rows={rows} />}
    </div>
  ) : (
    <ThrottlingMessage />
  );

  return cnvAnnotation && !isLoading ? (
    <React.Fragment>
      <div className="varsome-result-fixed">
        <div className="varsome-container varsome-container__basic">
          <ComponentPanel
            version={version}
            url={cnvHeaderUrl}
            title="CNV Classification"
            titleDetailsRight={getCNVDocumentationLink}
            content={content}
          />
        </div>
      </div>
    </React.Fragment>
  ) : null;
};

CnvACMG.propTypes = {
  variantQueryMode: PropTypes.shape({
    annotation_mode: PropTypes.oneOf(["", "germline", "somatic", ""]),
    phenotypes: PropTypes.oneOfType([
      PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string])
      ),
      PropTypes.string,
    ]),
    diseases: PropTypes.oneOfType([
      PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string])
      ),
      PropTypes.string,
    ]),
    zygosity: PropTypes.string,
    inheritance: PropTypes.string,
    age: PropTypes.string,
    ethnicity: PropTypes.string,
    sex: PropTypes.string,
    familyMembersAffected: PropTypes.string,
    familySegregation: PropTypes.string,
  }),
  acmgSampleFindings: PropTypes.shape({}),
  hasACMGErrors: PropTypes.bool,
  cnvClassificationErrors: PropTypes.arrayOf(PropTypes.string),
};

CnvACMG.defaultProps = {
  variantQueryMode: null,
  acmgSampleFindings: {},
  hasACMGErrors: false,
  cnvClassificationErrors: null,
};

const mapStateToProps = (state) => ({
  variantQueryMode: getVariantQueryMode(state),
  acmgSampleFindings: getCnvACMGAnnotationFindings(state),
  hasACMGErrors: getACMGClassifierHasErrors(state),
  cnvClassificationErrors: getCnvClassifierErrors(state),
});
export default connect(mapStateToProps)(
  withErrorGuard(CnvACMG, "CnvComponent", "CNV Classification")
);
